
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";

const HowItWorks = () => {
  return (
    <div className="flex flex-col items-start w-full min-h-screen bg-white font-inter">
      {/* Hero Section */}
      <section className="w-full h-[384px] bg-gradient-to-r from-[#ECFDF5] to-white">
        <div className="container mx-auto px-4 pt-32">
          <div className="max-w-[768px] mx-auto text-center space-y-8">
            <h1 className="text-4xl font-bold text-[#111827]">
              Transform Your Energy Data Into Actionable Insights
            </h1>
            <p className="text-lg text-[#4B5563]">
              Submit your energy consumption data and discover opportunities for significant cost savings through our advanced analytics platform.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section 1 */}
      <section className="w-full py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-[#111827] mb-16">
            A Simple Process...
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="relative">
              <img 
                src="/lovable-uploads/81dd3555-31ac-4b3c-bb53-2381ff7c940e.png" 
                alt="Energy data submission form showing fields for gas, electricity, and water bill details"
                className="w-full h-auto rounded-lg"
              />
            </div>
            <div className="space-y-8">
              <div className="bg-[#ECFDF5] p-6 rounded-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#059669] rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">Quick Upload</h3>
                    <p className="text-[#4B5563]">
                      Simply upload your energy bills or input your consumption data in less than 2 minutes
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#ECFDF5] p-6 rounded-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#059669] rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">User-friendly Interface</h3>
                    <p className="text-[#4B5563]">
                      Our user-friendly form guides you through each step with clear instructions and helpful tooltips
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#ECFDF5] p-6 rounded-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#059669] rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">Free submission</h3>
                    <p className="text-[#4B5563]">
                      We don't charge our customers for their energy report, our insights are free.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section 2 */}
      <section className="w-full py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-[#111827] mb-16">
            ...to get Actionable Results
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="bg-[#ECFDF5] p-6 rounded-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#059669] rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">Unbiased recommendations</h3>
                    <p className="text-[#4B5563]">
                      We are independent and are not paid to promote any providers or products.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#ECFDF5] p-6 rounded-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#059669] rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">Intuitive Results</h3>
                    <p className="text-[#4B5563]">
                      No technicalities and easy to understand results.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#ECFDF5] p-6 rounded-xl">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#059669] rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">Cost saving Insights</h3>
                    <p className="text-[#4B5563]">
                      Get the full view of what you can do to save money and easily act on it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/lovable-uploads/162fe732-34ce-4a8a-bd54-0334a56495b8.png" 
                alt="Detailed energy report showing cost analysis and recommendations" 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full bg-[#059669] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Saving?
          </h2>
          <p className="text-xl text-[#D1FAE5] mb-8">
            Generate your personalised energy insights report today and discover how much you could save.
          </p>
          <Button 
            className="h-[52px] px-8 text-lg bg-white text-[#059669] hover:bg-white/90"
            asChild
          >
            <Link to="/auth">Generate Your Free Insights</Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HowItWorks;
